/* Warteschlange: laufende und wartende Compile-Jobs (Benutzer: die eigenen mit Platz, Admin: alle mit Besitzer) und der Verlauf.
 * Zeilen der eigenen Jobs lassen sich im Compile-Dialog öffnen, aktive Jobs abbrechen (Admins auch fremde). */
import { useEffect } from 'react';
import type { CompileJob, QueueItem } from '../net/backend.ts';
import { elapsedSec, fmtDuration } from '../net/compile.ts';
import type { QueueLink } from '../net/queue.ts';
import { Dialog, DialogBody, DialogFoot } from './dialog.tsx';
import { useStore, useT } from './hooks.ts';

export interface QueueHost {
  admin: boolean;
  /** Kennung des angemeldeten Benutzers (eigene Jobs lassen sich öffnen) */
  userId: string;
  /** Job im Compile-Dialog öffnen */
  openJob(job: CompileJob): void;
}

const STATE_KEYS: Record<string, string> = {
  queued: 'compile.state_queued', running: 'compile.state_running', ok: 'compile.state_ok', failed: 'compile.state_failed',
  cancelled: 'compile.state_cancelled', timeout: 'compile.state_timeout', interrupted: 'compile.state_interrupted'
};

export function QueueDialog(props: { queue: QueueLink; host: QueueHost; onClose: () => void }) {
  const t = useT();
  const { queue, host } = props;
  const snap = useStore(queue);
  useEffect(() => { queue.start(); return () => queue.stop(); }, [queue]);
  const info = snap.info, now = Date.now();

  const place = (j: QueueItem): string => j.position > 0 ? t('queue.place', { n: j.position }) : t('compile.state_running') + (j.step ? ' · ' + j.step : '');
  const cancel = (j: QueueItem) => { if (j.state === 'running' && !window.confirm(t('compile.cancel_confirm'))) return; void queue.cancel(j.id); };
  const open = (j: CompileJob) => j.owner === host.userId ? <button className="btn sm" onClick={() => host.openJob(j)}>{t('queue.open')}</button> : null;
  const who = (j: CompileJob) => host.admin ? <td>{j.ownerEmail || j.owner}</td> : null;

  return (
    <Dialog title={t('queue.title')} onClose={props.onClose}>
      <DialogBody>
        {info && <p className="small" role="status">{t('queue.summary', { running: info.running, slots: info.slots, queued: info.queued })}</p>}
        <h3>{host.admin ? t('queue.active_all') : t('queue.active_mine')}</h3>
        {info && info.items.length === 0 && <p className="small">{t('queue.empty')}</p>}
        {info && info.items.length > 0 && (
          <table className="qtable" aria-label={t('queue.active_mine')}>
            <thead><tr><th>{t('queue.col_status')}</th>{host.admin && <th>{t('queue.col_user')}</th>}<th>{t('queue.col_map')}</th><th>{t('queue.col_profile')}</th><th>{t('queue.col_time')}</th><th /></tr></thead>
            <tbody>
              {info.items.map((j) => (
                <tr key={j.id} data-state={j.state}>
                  <td>{place(j)}</td>{who(j)}<td>{j.mapName}</td><td>{j.profile}{j.engine === 'rust' ? ' · Rust' : ''}</td>
                  <td>{j.started ? fmtDuration(elapsedSec(j, now)) : '–'}</td>
                  <td>{open(j)} <button className="btn sm" disabled={snap.busy === j.id} onClick={() => cancel(j)}>{t('compile.cancel')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <h3>{t('queue.history')}</h3>
        {snap.history.length === 0 ? <p className="small">{t('queue.history_empty')}</p> : (
          <table className="qtable" aria-label={t('queue.history')}>
            <tbody>
              {snap.history.map((j) => (
                <tr key={j.id} data-state={j.state}>
                  <td>{t(STATE_KEYS[j.state])}</td>{who(j)}<td>{j.mapName}</td><td>{j.profile}{j.engine === 'rust' ? ' · Rust' : ''}</td>
                  <td>{j.started ? fmtDuration(elapsedSec(j, now)) : '–'}</td><td>{open(j)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {snap.error && <p className="small" role="alert">{snap.error}</p>}
      </DialogBody>
      <DialogFoot><button className="btn" onClick={props.onClose}>{t('ui.close')}</button></DialogFoot>
    </Dialog>
  );
}
